import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { ArrowLeft, Edit3, UserCheck, UserX, Phone, Mail, GraduationCap } from 'lucide-react';
import Drawer from '../../components/Drawer';

const STATUS_OPTIONS = ['active', 'inactive'];
const RELATIONS = ['Father', 'Mother', 'Guardian', 'Other'];

function StatusPill({ status }) {
  const cls = status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{status || 'active'}</span>;
}

/* ── Edit Student Drawer ── */
function EditStudentDrawer({ open, onClose, student, token, onSuccess }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [parents, setParents] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && student) {
      setForm({ name: student.name || '', phone: student.phone || '', email: student.email || '', admission_no: student.admission_no || '', grade: student.grade || '', batch: student.batch || '' });
      setParents((student.parents || []).map(p => ({ ...p })));
    }
  }, [open, student]);

  const setParent = (i, k, v) => setParents(p => { const c = [...p]; c[i] = { ...c[i], [k]: v }; return c; });

  const submit = async () => {
    setSaving(true);
    try {
      const validParents = parents.filter(p => p.name?.trim() && p.phone?.trim());
      await api.customerPatch(token, student.id, { ...form, parents: validParents.length > 0 ? validParents : undefined });
      toast.show('Student updated', 'success');
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Update failed', 'error'); } finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Edit Student" wide>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <label className="block col-span-2"><span className="text-xs font-bold text-gray-500 uppercase">Name</span>
            <input data-testid="edit-student-name" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.name || ''} onChange={e => set('name', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Phone</span>
            <input data-testid="edit-student-phone" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Email</span>
            <input data-testid="edit-student-email" type="email" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.email || ''} onChange={e => set('email', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Admission No</span>
            <input data-testid="edit-student-admission" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.admission_no || ''} onChange={e => set('admission_no', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Grade</span>
            <input data-testid="edit-student-grade" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.grade || ''} onChange={e => set('grade', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Batch</span>
            <input data-testid="edit-student-batch" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.batch || ''} onChange={e => set('batch', e.target.value)} /></label>
        </div>
        {/* Parents */}
        <div>
          <span className="text-xs font-bold text-gray-500 uppercase mb-2 block">Parents / Guardians</span>
          {parents.map((p, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 mb-3 p-3 bg-gray-50 rounded-xl">
              <input placeholder="Name" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" value={p.name || ''} onChange={e => setParent(i, 'name', e.target.value)} />
              <input placeholder="Phone" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" value={p.phone || ''} onChange={e => setParent(i, 'phone', e.target.value)} />
              <select className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" value={p.relation || ''} onChange={e => setParent(i, 'relation', e.target.value)}>
                {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input placeholder="Email" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" value={p.email || ''} onChange={e => setParent(i, 'email', e.target.value)} />
            </div>
          ))}
        </div>
        <button onClick={submit} disabled={saving} data-testid="edit-student-submit"
          className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm disabled:opacity-50 hover:shadow-lg transition-all">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </Drawer>
  );
}

export default function StudentDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [student, setStudent] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const s = await api.customerGet(token, id);
      setStudent(s);
      // Fetch linked sales (where customer_id = this student)
      try {
        const sr = await api.salesList(token, { customer_id: id });
        setSales((sr.items || []).slice(0, 20));
      } catch { setSales([]); }
    } catch (e) { toast.show(e?.message || 'Load failed', 'error'); }
    finally { setLoading(false); }
  }, [token, id, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async () => {
    if (!student) return;
    const newStatus = student.active_status === 'active' ? 'inactive' : 'active';
    try {
      await api.customerPatch(token, id, { active_status: newStatus });
      toast.show(`Student ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
      load();
    } catch (e) { toast.show(e?.message || 'Update failed', 'error'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>;
  if (!student) return <div className="p-8 text-center text-gray-500">Student not found</div>;

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="student-detail-page">
      <button onClick={() => navigate('/school/students')} data-testid="student-back" className="flex items-center gap-2 text-gray-500 text-sm font-semibold mb-5 hover:text-gray-700"><ArrowLeft size={16} /> Back to Students</button>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-6" data-testid="student-header">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center"><GraduationCap size={24} className="text-indigo-600" /></div>
              <div>
                <h1 className="text-xl font-extrabold text-gray-900">{student.name}</h1>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                  {student.admission_no && <span className="font-mono">{student.admission_no}</span>}
                  {student.grade && <span>Grade {student.grade}</span>}
                  {student.batch && <span>Batch {student.batch}</span>}
                </div>
              </div>
            </div>
            <StatusPill status={student.active_status} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditOpen(true)} data-testid="student-edit-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50"><Edit3 size={14} /> Edit</button>
            <button onClick={toggleStatus} data-testid="student-toggle-status"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border text-sm font-semibold ${student.active_status === 'active' ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'}`}>
              {student.active_status === 'active' ? <><UserX size={14} /> Deactivate</> : <><UserCheck size={14} /> Activate</>}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Parents */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="student-parents">
          <h3 className="text-gray-500 text-xs font-bold uppercase mb-3">Parents / Guardians</h3>
          {(student.parents || []).length === 0 ? <p className="text-gray-400 text-sm">No parent records</p> : (
            <div className="space-y-3">
              {student.parents.map((p, i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl" data-testid={`parent-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">{p.name?.[0]?.toUpperCase() || '?'}</div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-semibold text-sm">{p.name} {p.relation && <span className="text-gray-400 text-xs font-normal">({p.relation})</span>}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      {p.phone && <span className="flex items-center gap-1"><Phone size={10} />{p.phone}</span>}
                      {p.email && <span className="flex items-center gap-1"><Mail size={10} />{p.email}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact info */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="student-contact">
          <h3 className="text-gray-500 text-xs font-bold uppercase mb-3">Contact Info</h3>
          <div className="space-y-2 text-sm">
            {student.phone && <p className="flex items-center gap-2 text-gray-700"><Phone size={14} className="text-gray-400" /> {student.phone}</p>}
            {student.email && <p className="flex items-center gap-2 text-gray-700"><Mail size={14} className="text-gray-400" /> {student.email}</p>}
            {student.gstin && <p className="text-gray-500">GSTIN: {student.gstin}</p>}
          </div>
        </div>

        {/* Linked sales (invoices) */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="student-sales">
          <h3 className="text-gray-500 text-xs font-bold uppercase mb-3">Linked Invoices</h3>
          {sales.length === 0 ? <p className="text-gray-400 text-sm">No invoices found for this student</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-200">
                <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Invoice</th>
                <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Date</th>
                <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Amount</th>
                <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Status</th>
              </tr></thead>
              <tbody>
                {sales.map(s => (
                  <tr key={s.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/sales')}>
                    <td className="px-4 py-2 text-gray-900 font-semibold">{s.invoice_number || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{s.sale_date ? new Date(s.sale_date).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="px-4 py-2 text-gray-900 text-right tabular-nums font-semibold">{formatMoney(s.total)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${s.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : s.payment_status === 'partial' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{s.payment_status || 'unpaid'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <EditStudentDrawer open={editOpen} onClose={() => setEditOpen(false)} student={student} token={token} onSuccess={() => { setEditOpen(false); load(); }} />
    </div>
  );
}
